import { apiFetchJSON } from '../../core/api.js';
import { getTg } from '../../core/telegram.js';
import { getOwnerChatActiveConversationUserId } from './ui-state.js';
import { ownerChatMessagesEl } from './dom.js';
import { removeOwnerChatMessageById } from './messages.js';
import { startOwnerChatEdit } from './edit-mode.js';

const LONG_PRESS_MS = 520;
const MOVE_CANCEL_PX = 12;

let pressTimer: ReturnType<typeof setTimeout> | null = null;
let pressRow: HTMLElement | null = null;
let pressStartX = 0;
let pressStartY = 0;
let activeMenuEl: HTMLElement | null = null;
let activeBackdropEl: HTMLElement | null = null;

function clearPressTimer() {
  if (pressTimer) {
    clearTimeout(pressTimer);
    pressTimer = null;
  }
  pressRow = null;
}

function closeMessageMenu() {
  if (activeMenuEl) {
    try {
      activeMenuEl.remove();
    } catch (_) {}
    activeMenuEl = null;
  }
  if (activeBackdropEl) {
    try {
      activeBackdropEl.remove();
    } catch (_) {}
    activeBackdropEl = null;
  }
}

function getOwnMessageRow(target: EventTarget | null): HTMLElement | null {
  if (!target || !(target instanceof Element)) return null;
  const row = target.closest('.chat-msg-row-me[data-msg-id]');
  return row instanceof HTMLElement ? row : null;
}

function getMessageTextFromRow(row: HTMLElement): string {
  const textEl = row.querySelector('.chat-bubble-text');
  return textEl ? String(textEl.textContent || '') : '';
}

function rowHasEditableText(row: HTMLElement): boolean {
  return !!getMessageTextFromRow(row).length || !!row.querySelector('.chat-bubble-text');
}

function rowHasPhoto(row: HTMLElement): boolean {
  return !!row.querySelector('.chat-bubble-image');
}

function confirmDelete(onConfirm: () => void) {
  const question = 'Удалить сообщение?';
  try {
    const tg = getTg();
    if (tg && typeof tg.showConfirm === 'function') {
      tg.showConfirm(question, function (confirmed: boolean) {
        if (confirmed) onConfirm();
      });
      return;
    }
  } catch (_) {}
  if (window.confirm(question)) onConfirm();
}

async function deleteOwnerChatMessage(messageId: string) {
  const conversationUserId = getOwnerChatActiveConversationUserId();
  if (!conversationUserId) return;

  const data = await apiFetchJSON<{ ok: boolean }>('/api/owner-chat/delete-message', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversationUserId, messageId })
  }).catch(function () {
    return null;
  });

  if (!data || !data.ok) {
    try {
      const tg = getTg();
      if (tg && typeof tg.showAlert === 'function') {
        tg.showAlert('Не удалось удалить сообщение');
        return;
      }
    } catch (_) {}
    return;
  }

  removeOwnerChatMessageById(messageId);
}

function openMessageMenu(row: HTMLElement, anchorX: number, anchorY: number) {
  const msgId = row.getAttribute('data-msg-id');
  if (!msgId || msgId === 'consult-welcome') return;

  closeMessageMenu();

  const canEdit = rowHasEditableText(row);
  const hasPhoto = rowHasPhoto(row);

  const backdrop = document.createElement('div');
  backdrop.className = 'chat-msg-menu-backdrop';
  backdrop.addEventListener('click', closeMessageMenu);
  backdrop.addEventListener('contextmenu', function (e) {
    e.preventDefault();
    closeMessageMenu();
  });

  const menu = document.createElement('div');
  menu.className = 'chat-msg-menu';
  menu.setAttribute('role', 'menu');

  if (canEdit) {
    const editBtn = document.createElement('button');
    editBtn.type = 'button';
    editBtn.className = 'chat-msg-menu-item';
    editBtn.setAttribute('role', 'menuitem');
    editBtn.textContent = 'Редактировать';
    editBtn.addEventListener('click', function () {
      closeMessageMenu();
      startOwnerChatEdit(msgId, getMessageTextFromRow(row));
    });
    menu.appendChild(editBtn);
  }

  const deleteBtn = document.createElement('button');
  deleteBtn.type = 'button';
  deleteBtn.className = 'chat-msg-menu-item chat-msg-menu-item--danger';
  deleteBtn.setAttribute('role', 'menuitem');
  deleteBtn.textContent = hasPhoto && !canEdit ? 'Удалить фото' : 'Удалить';
  deleteBtn.addEventListener('click', function () {
    closeMessageMenu();
    confirmDelete(function () {
      deleteOwnerChatMessage(msgId).catch(function () {});
    });
  });
  menu.appendChild(deleteBtn);

  document.body.appendChild(backdrop);
  document.body.appendChild(menu);

  const rect = row.getBoundingClientRect();
  const menuRect = menu.getBoundingClientRect();
  const pad = 8;
  let left = Math.min(Math.max(pad, anchorX - menuRect.width / 2), window.innerWidth - menuRect.width - pad);
  let top = rect.top - menuRect.height - 10;
  if (top < pad) top = Math.min(rect.bottom + 10, window.innerHeight - menuRect.height - pad);

  menu.style.left = left + 'px';
  menu.style.top = top + 'px';

  activeMenuEl = menu;
  activeBackdropEl = backdrop;
}

function onPointerDown(e: PointerEvent) {
  if (e.button !== 0) return;
  const row = getOwnMessageRow(e.target);
  if (!row) return;
  if (activeMenuEl) return;

  pressRow = row;
  pressStartX = e.clientX;
  pressStartY = e.clientY;
  clearPressTimer();
  pressTimer = setTimeout(function () {
    if (!pressRow) return;
    const rowRef = pressRow;
    pressRow = null;
    pressTimer = null;
    try {
      if (navigator.vibrate) navigator.vibrate(12);
    } catch (_) {}
    openMessageMenu(rowRef, pressStartX, pressStartY);
  }, LONG_PRESS_MS);
}

function onPointerMove(e: PointerEvent) {
  if (!pressTimer || !pressRow) return;
  const dx = Math.abs(e.clientX - pressStartX);
  const dy = Math.abs(e.clientY - pressStartY);
  if (dx > MOVE_CANCEL_PX || dy > MOVE_CANCEL_PX) clearPressTimer();
}

function onPointerUp() {
  clearPressTimer();
}

function onContextMenu(e: MouseEvent) {
  const row = getOwnMessageRow(e.target);
  if (!row) return;
  e.preventDefault();
  openMessageMenu(row, e.clientX, e.clientY);
}

export function bindOwnerChatMessageActions(): void {
  if (!ownerChatMessagesEl) return;

  ownerChatMessagesEl.addEventListener('pointerdown', onPointerDown);
  ownerChatMessagesEl.addEventListener('pointermove', onPointerMove);
  ownerChatMessagesEl.addEventListener('pointerup', onPointerUp);
  ownerChatMessagesEl.addEventListener('pointercancel', onPointerUp);
  ownerChatMessagesEl.addEventListener('contextmenu', onContextMenu);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeMessageMenu();
  });
}
